import { Fragment, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, post, put, type CaptionsAPIKey, type CaptionsJobAdmin, type CaptionsModelInfo, type CaptionsProvider, type CaptionsWorkerInfo, type NewCaptionsWorker, type WorkerSample } from '../../api'
import { Button, Copyable, Field } from '../../components/ui'
import { langName } from '../../live-ui/captions'
import { useMedia } from '../../live-ui/studio/useLiveScreen'
import { TXT, type StateColor } from '../../live-ui/ui'
import { LineChart, onAxis } from './LineChart'
import { bps, ago, dur } from './format'
import { GearIcon } from './StatusCell'
import type { Spec } from './states'
import { Empty, ErrorBox, Fact, NetPanel, SkeletonRows, StateBadge, StateText } from './ui'
import { AccountSelect, COSTS_KEY, CostAccountsPanel, usd, useCosts, WorkerCostBlock, WorkerCostCell } from './CaptionsCosts'
import type { CostAccount } from '../../api'
import { MachinesPanel } from './CaptionsMachines'

/*
 * Network › Captions (owner 21.10): the nodes that recognize speech for
 * captions — the workers (docs/captions-overlay.md §10.3) — and their jobs,
 * moved here from the separate Captions section. Per node: kind (GPU, CPU,
 * external API), state, slots, its machine (CPU, RAM, video card load,
 * memory and temperature), traffic in and out, models with type and size,
 * and the slowest job's real-time factor. The gear opens the details under
 * the row: the machine, 5 minutes of charts, models, jobs, revoke. "Add
 * worker" issues a token (shown once). An older worker that sends no
 * machine data shows "—".
 * Desktop: a table that fits 1280 px (fixed layout); phone: cards.
 *
 * API keys (owner 21.10): an external service's key is stored by the center
 * (sealed) and given to the API nodes the admin assigns it to; one key may
 * serve several nodes or each node its own — the panel shows which nodes use
 * which key. The console only ever sees the label and the last characters.
 *
 * Costs (owner 21.10, CaptionsCosts.tsx): a Cost column (this month, today),
 * the Cost accounts panel, an account per key (audio) and per node (machine
 * hours); a node of an account stopped by its budget takes no new jobs.
 */

export const WORKERS_KEY = ['admin-captions-workers']
export const API_KEYS_KEY = ['admin-captions-api-keys']

export const PROVIDERS: Record<CaptionsProvider, string> = {
  assemblyai: 'AssemblyAI',
  soniox: 'Soniox',
  deepgram: 'Deepgram',
  openai: 'OpenAI',
}
const SEL = 'min-h-9 rounded-md border border-line-2 bg-bg px-2 text-[13px] text-text-soft focus-visible:border-accent max-md:min-h-[44px]'
/** an API node that is connected but has no key takes no jobs */
/** " · Deepgram" after an API node's id */
const svc = (w: CaptionsWorkerInfo) => (w.kind === 'api' && w.provider ? ` · ${PROVIDERS[w.provider] ?? w.provider}` : '')
const keyMissing = (w: CaptionsWorkerInfo) => w.kind === 'api' && w.connected && !w.revoked_at && w.key_ready === false

const SPEC: Record<'connected' | 'offline' | 'revoked', Spec> = {
  connected: { glyph: '●', word: 'Connected', color: 'live', border: 'solid', hint: 'The worker has its channel to the center' },
  offline: { glyph: '○', word: 'Offline', color: 'idle', border: 'dotted', hint: 'No channel: its jobs wait 60 s, then move to another worker' },
  revoked: { glyph: '✕', word: 'Revoked', color: 'idle', border: 'solid', hint: 'Its token no longer works' },
}
const stateOf = (w: CaptionsWorkerInfo) => (w.revoked_at ? 'revoked' : w.connected ? 'connected' : 'offline')

const JOB_SPEC: Record<CaptionsJobAdmin['state'], Spec> = {
  live: { glyph: '●', word: 'Live', color: 'live', border: 'solid' },
  starting: { glyph: '◔', word: 'Starting', color: 'start', border: 'dashed' },
  stalled: { glyph: '▲', word: 'Stalled', color: 'warn', border: 'dashed', hint: 'No audio or the worker falls behind' },
  no_worker: { glyph: '○', word: 'No worker', color: 'warn', border: 'dotted' },
  rejected: { glyph: '✕', word: 'Refused by YouTube', color: 'fail', border: 'solid' },
}

const KIND: Record<string, { label: string; hint: string }> = {
  gpu: { label: 'GPU', hint: 'Recognizes on its own video card' },
  cpu: { label: 'CPU', hint: 'Recognizes on its CPU (small models, debugging)' },
  api: { label: 'API', hint: 'Relays the audio to an external recognition service' },
}
const MODEL_TYPE: Record<CaptionsModelInfo['type'], string> = {
  asr: 'Speech recognition',
  vad: 'Voice activity',
  lid: 'Language detection',
  second_pass: 'Second pass',
  mt: 'Translation',
  other: 'Other',
}

/** 9 100 MB → "8.9 GB"; 512 → "512 MB". */
export const mb = (v: number | null | undefined) => (v == null ? '—' : v >= 1024 ? `${(v / 1024).toFixed(v >= 10240 ? 0 : 1)} GB` : `${Math.round(v)} MB`)
/** bytes → "1.2 GB" / "340 MB" / "12 kB". */
export const bytes = (v: number | null | undefined) =>
  v == null ? '—' : v >= 1 << 30 ? `${(v / (1 << 30)).toFixed(1)} GB` : v >= 1 << 20 ? `${(v / (1 << 20)).toFixed(1)} MB` : v >= 1024 ? `${Math.round(v / 1024)} kB` : `${v} B`
const pctText = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v)}%`)
const load = (v: number | null | undefined): StateColor => (v == null ? 'idle' : v >= 90 ? 'fail' : v >= 75 ? 'warn' : 'live')

/** A percentage as a thin bar + figure; colour only adds to the number. */
function Meter({ v, label, sub, testId }: { v?: number | null; label: string; sub?: ReactNode; testId?: string }) {
  const c = load(v)
  // nothing reported: a dash, not an empty bar that reads as 0 %
  if (v == null && !sub) return <span className="text-muted" data-testid={testId} title={`${label}: not reported`}>—</span>
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={testId} title={label}>
      <span className="flex items-baseline gap-1.5">
        <span className={`num font-mono text-[13px] ${v == null ? 'text-muted' : 'text-text'}`}>{pctText(v)}</span>
        {v != null && v >= 75 && <span className={`text-[11px] ${TXT[c]}`} aria-hidden>▲</span>}
      </span>
      {v != null && <span className="h-1 w-full max-w-24 overflow-hidden rounded-full bg-line" aria-hidden>
        <span className={`block h-full ${c === 'fail' ? 'bg-fail' : c === 'warn' ? 'bg-warn' : 'bg-live'}`} style={{ width: `${Math.max(2, Math.min(100, v))}%` }} />
      </span>}
      {sub && <span className="num truncate font-mono text-[11px] text-muted">{sub}</span>}
    </div>
  )
}

function KindTag({ w, testId }: { w: CaptionsWorkerInfo; testId?: string }) {
  const k = w.kind ? KIND[w.kind] : undefined
  if (!k) return null
  return (
    <span title={k.hint} data-testid={testId} className="flex-none rounded-sm border border-line-2 px-1 font-mono text-[10px] leading-4 font-semibold tracking-[.06em] text-text-soft">
      {k.label}
    </span>
  )
}

const modelsTotal = (w: CaptionsWorkerInfo) => (w.model_info ?? []).reduce((a, m) => a + (m.size_mb ?? 0), 0)
function modelsText(w: CaptionsWorkerInfo): string {
  const list = w.model_info
  if (!list?.length) return w.models.length ? `${w.models.length}` : '—'
  const total = modelsTotal(w)
  return `${list.length}${total ? ` · ${mb(total)}` : ''}`
}

function Traffic({ w, testId }: { w: CaptionsWorkerInfo; testId?: string }) {
  const r = w.res
  if (r?.rx_bps == null && r?.tx_bps == null) return <span className="text-muted" data-testid={testId} title="Not reported yet">—</span>
  return (
    <div className="num flex flex-col font-mono text-[12px] leading-5 whitespace-nowrap" data-testid={testId} title="Audio in from the media nodes · captions out, last 5 s">
      <span>
        <span className="text-muted" aria-label="in">↓</span> {bps(r?.rx_bps != null ? Math.round(r.rx_bps) : null)}
      </span>
      <span>
        <span className="text-muted" aria-label="out">↑</span> {bps(r?.tx_bps != null ? Math.round(r.tx_bps) : null)}
      </span>
    </div>
  )
}

function Slots({ w, testId }: { w: CaptionsWorkerInfo; testId?: string }) {
  if (!w.connected) return <span className="text-muted">—</span>
  const busy = w.jobs.length
  return (
    <span className="num font-mono text-[13px]" data-testid={testId}>
      {busy}
      <span className="text-muted"> / {w.slots}</span>
    </span>
  )
}

export function NetworkCaptionsPage() {
  const qc = useQueryClient()
  const workers = useQuery({ queryKey: WORKERS_KEY, queryFn: () => get<CaptionsWorkerInfo[]>('/api/admin/captions/workers'), refetchInterval: 3000 })
  const keys = useQuery({ queryKey: API_KEYS_KEY, queryFn: () => get<CaptionsAPIKey[]>('/api/admin/captions/api-keys') })
  const costs = useCosts()
  const accounts = costs.data?.accounts ?? []
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [issued, setIssued] = useState<NewCaptionsWorker | null>(null)
  const [confirm, setConfirm] = useState<string | null>(null)
  const [err, setErr] = useState('')
  const desktop = useMedia('(min-width: 768px)')

  const issue = async () => {
    setErr('')
    try {
      const w = await post<NewCaptionsWorker>('/api/admin/captions/workers', { name: name.trim() })
      setIssued(w)
      setName('')
      await qc.invalidateQueries({ queryKey: WORKERS_KEY })
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  const revoke = async (id: string) => {
    setErr('')
    try {
      await post(`/api/admin/captions/workers/${id}/revoke`)
      setConfirm(null)
      if (issued?.id === id) setIssued(null)
      await qc.invalidateQueries({ queryKey: WORKERS_KEY })
    } catch (e) {
      setErr((e as Error).message)
    }
  }

  const list = workers.data ?? []
  const active = list.filter((w) => !w.revoked_at)
  const connected = active.filter((w) => w.connected)
  const slots = connected.reduce((n, w) => n + w.slots, 0)
  const busy = connected.reduce((n, w) => n + w.jobs.length, 0)
  const jobs = list.flatMap((w) => w.jobs.map((j) => ({ ...j, worker: w })))
  const toggle = (id: string) => {
    setConfirm(null)
    setOpen((o) => (o === id ? null : id))
  }
  const refreshKeys = () =>
    Promise.all([qc.invalidateQueries({ queryKey: API_KEYS_KEY }), qc.invalidateQueries({ queryKey: WORKERS_KEY }), qc.invalidateQueries({ queryKey: COSTS_KEY })])
  const setAccount = async (path: string, id: string | null) => {
    setErr('')
    try {
      await put(path, { account_id: id })
      await refreshKeys()
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  const assign = async (id: string, keyID: string | null) => {
    setErr('')
    try {
      await put(`/api/admin/captions/workers/${id}/api-key`, { key_id: keyID })
      await refreshKeys()
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  const details = (w: CaptionsWorkerInfo) => (
    <WorkerDetails w={w} keys={keys.data ?? []} accounts={accounts} onAssign={(k) => assign(w.id, k)} onAccount={(a) => setAccount(`/api/admin/captions/workers/${w.id}/account`, a)} confirm={confirm === w.id} onConfirm={(v) => setConfirm(v ? w.id : null)} onRevoke={() => void revoke(w.id)} />
  )

  return (
    <div className="flex flex-col gap-4" data-testid="network-captions">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Captions</h1>
        <span className="num text-sm text-muted" data-testid="captions-capacity">
          {connected.length} connected · {busy} of {slots} slots busy
        </span>
        <span className="ml-auto">
          <Button variant="primary" data-testid="worker-add" aria-expanded={adding} className="max-md:min-h-[44px]" onClick={() => setAdding((a) => !a)}>
            {adding ? 'Close' : 'Add worker'}
          </Button>
        </span>
      </div>
      {err && <ErrorBox text={err} />}
      {adding && <IssuePanel name={name} setName={setName} issued={issued} onIssue={() => void issue()} />}
      {workers.error && <ErrorBox text={(workers.error as Error).message} onRetry={() => void workers.refetch()} busy={workers.isFetching} />}

      <NetPanel pad={false} title="Nodes" aside={<span className="text-xs text-muted">Workers that recognize speech for captions</span>}>
        {workers.isPending ? (
          <div className="p-4">
            <SkeletonRows rows={3} cols={8} />
          </div>
        ) : list.length === 0 ? (
          <div className="p-4">
            <Empty title="No captions workers yet" testId="workers-empty">
              Add a worker: it gets a token, starts on a machine with a video card (or with an external recognition service) and connects to the center by itself.
            </Empty>
          </div>
        ) : (
          desktop ? (
            <WorkersTable list={list} open={open} onToggle={toggle} details={details} />
          ) : (
            <ul className="divide-y divide-line" data-testid="workers-cards">
              {list.map((w) => (
                <WorkerCard key={w.id} w={w} open={open === w.id} onToggle={() => toggle(w.id)}>
                  {open === w.id && details(w)}
                </WorkerCard>
              ))}
            </ul>
          )
        )}
      </NetPanel>

      <MachinesPanel accounts={accounts} />

      <APIKeysPanel keys={keys.data} loading={keys.isPending} error={keys.error as Error | null} workers={list} onChanged={refreshKeys}
        accounts={accounts} onAccount={(k, a) => setAccount(`/api/admin/captions/api-keys/${k}/account`, a)} />

      <CostAccountsPanel costs={costs} keys={keys.data ?? []} workers={list} onChanged={refreshKeys} />

      <NetPanel pad={false} title="Jobs" aside={<span className="num text-xs text-muted">{jobs.length}</span>} testId="captions-jobs">
        {jobs.length === 0 ? (
          <p className="p-4 text-sm text-muted">No captions jobs: a stream gets one when captions are on and one of its YouTube outputs sends them.</p>
        ) : (
          <JobsTable jobs={jobs} />
        )}
      </NetPanel>
    </div>
  )
}

function IssuePanel({ name, setName, issued, onIssue }: { name: string; setName: (v: string) => void; issued: NewCaptionsWorker | null; onIssue: () => void }) {
  return (
    <NetPanel title="Add a worker" testId="worker-add-panel">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          onIssue()
        }}
      >
        <div className="min-w-48 flex-1">
          <Field label="Worker name" name="worker-name" value={name} maxLength={64} onChange={(e) => setName(e.target.value)} placeholder="e.g. Eugene's PC (RTX 4080)" />
        </div>
        <Button variant="primary" type="submit" disabled={!name.trim()}>
          Issue token
        </Button>
      </form>
      {issued && (
        <div className="mt-4 space-y-2" data-testid="worker-issued">
          <p className="text-sm">Start the worker with these. The token is shown only now; issue a new worker if it is lost. Revoking stops it at once.</p>
          <Copyable label="Worker id" value={issued.id} />
          <Copyable label="Worker token" value={issued.token} />
        </div>
      )}
    </NetPanel>
  )
}

const COLS = [
  { key: 'node', label: 'Node', w: 17 },
  { key: 'state', label: 'State', w: 11 },
  { key: 'slots', label: 'Slots', w: 6 },
  { key: 'cpu', label: 'CPU', w: 8 },
  { key: 'ram', label: 'RAM', w: 11 },
  { key: 'gpu', label: 'Video card', w: 15 },
  { key: 'net', label: 'Traffic', w: 10 },
  { key: 'models', label: 'Models', w: 9 },
  { key: 'rtf', label: 'RTF', w: 5 },
  { key: 'cost', label: 'Cost', w: 8 },
  { key: 'act', label: '', w: 4 },
]
const COLS_SUM = COLS.reduce((a, c) => a + c.w, 0)

function WorkersTable({ list, open, onToggle, details }: { list: CaptionsWorkerInfo[]; open: string | null; onToggle: (id: string) => void; details: (w: CaptionsWorkerInfo) => ReactNode }) {
  return (
    <div>
      <table data-testid="workers-table" className="w-full table-fixed text-left text-sm">
        <colgroup>
          {COLS.map((c) => (
            <col key={c.key} style={{ width: `${((c.w / COLS_SUM) * 100).toFixed(2)}%` }} />
          ))}
        </colgroup>
        <thead className="border-b border-line text-xs text-muted">
          <tr>
            {COLS.map((c) => (
              <th scope="col" key={c.key} className="px-3 py-2 font-medium whitespace-nowrap first:pl-4 last:pr-4">
                {c.key === 'act' ? <span className="sr-only">Details</span> : c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {list.map((w) => {
            const r = w.res
            const st = stateOf(w)
            const on = w.connected && !w.revoked_at
            return (
              <Fragment key={w.id}>
                <tr data-testid={`worker-${w.id}`} data-state={st} className={`[&>td]:overflow-hidden [&>td]:px-3 [&>td]:py-2.5 [&>td]:align-top [&>td:first-child]:pl-4 [&>td:last-child]:pr-4 ${w.revoked_at ? 'opacity-60' : ''} ${open === w.id ? 'bg-panel shadow-[inset_2px_0_0_var(--accent)]' : ''}`}>
                  <td>
                    <p className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate font-medium" title={w.name}>{w.name}</span>
                      <KindTag w={w} testId={`worker-kind-${w.id}`} />
                    </p>
                    <p className="truncate font-mono text-xs text-muted" title={w.gpu || undefined}>
                      {w.id}
                      {svc(w)}
                      {w.version ? ` · v${w.version}` : ''}
                    </p>
                  </td>
                  <td>
                    <StateBadge spec={SPEC[st]} testId={`worker-state-${w.id}`} />
                    <p className="num mt-1 truncate text-xs text-muted" data-testid={`worker-facts-${w.id}`}>
                      {w.revoked_at ? ago(w.revoked_at) : w.connected ? dur(w.connected_since) : `seen ${ago(w.last_seen_at)}`}
                    </p>
                    {keyMissing(w) && <p className="mt-0.5 truncate text-xs text-warn" data-testid={`worker-nokey-${w.id}`} title="Assign a key in its details">▲ No key: no jobs</p>}
                    {w.cost_stopped && <p className="mt-0.5 truncate text-xs text-fail" data-testid={`worker-budget-${w.id}`} title="Its cost account is over the monthly budget: no new jobs, running ones stay">■ Budget stop</p>}
                  </td>
                  <td><Slots w={w} testId={`worker-slots-${w.id}`} /></td>
                  <td>{on ? <Meter v={r?.cpu_pct} label="CPU of the whole machine" sub={w.host?.cpu_cores ? `${w.host.cpu_cores} cores` : undefined} testId={`worker-cpu-${w.id}`} /> : <span className="text-muted">—</span>}</td>
                  <td>
                    {on ? (
                      <Meter v={r?.ram_used_mb != null && r.ram_total_mb ? (r.ram_used_mb / r.ram_total_mb) * 100 : null} label="Memory in use"
                        sub={r?.ram_used_mb != null ? `${mb(r.ram_used_mb)} / ${mb(r.ram_total_mb ?? w.host?.ram_mb)}` : undefined} testId={`worker-ram-${w.id}`} />
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    {on && (w.gpu || r?.gpu_pct != null) ? (
                      <Meter v={r?.gpu_pct} label={`${w.gpu || 'Video card'}: load`}
                        sub={r?.gpu_mem_used_mb != null ? `${mb(r.gpu_mem_used_mb)} / ${mb(r.gpu_mem_mb ?? w.host?.gpu_mem_mb)}${r.gpu_temp_c != null ? ` · ${Math.round(r.gpu_temp_c)}°C` : ''}` : w.gpu || undefined}
                        testId={`worker-gpu-${w.id}`} />
                    ) : <span className="text-muted">{on && w.kind === 'api' ? 'none (API)' : '—'}</span>}
                  </td>
                  <td>{on ? <Traffic w={w} testId={`worker-net-${w.id}`} /> : <span className="text-muted">—</span>}</td>
                  <td className="num truncate font-mono text-[13px] whitespace-nowrap" data-testid={`worker-models-${w.id}`} title={modelsText(w)}>{modelsText(w)}</td>
                  <td className="num font-mono text-[13px]">
                    {w.rtf != null ? <span className={w.rtf >= 1 ? 'text-fail' : w.rtf >= 0.7 ? 'text-warn' : ''} title="Slowest live job: below 1 keeps up">{w.rtf.toFixed(2)}</span> : <span className="text-muted">—</span>}
                  </td>
                  <td><WorkerCostCell w={w} testId={`worker-cost-cell-${w.id}`} /></td>
                  <td className="text-right">
                    <button type="button" data-testid={`worker-open-${w.id}`} aria-expanded={open === w.id} aria-label={`Details of ${w.name}`} onClick={() => onToggle(w.id)}
                      className={`inline-grid h-8 w-8 place-items-center rounded-md border ${open === w.id ? 'border-accent text-accent' : 'border-line-2 text-muted hover:text-text'}`}>
                      <GearIcon />
                    </button>
                  </td>
                </tr>
                {open === w.id && (
                  <tr data-testid={`worker-details-row-${w.id}`} className="bg-panel">
                    <td colSpan={COLS.length} className="px-4 pt-1 pb-4">{details(w)}</td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function WorkerCard({ w, open, onToggle, children }: { w: CaptionsWorkerInfo; open: boolean; onToggle: () => void; children: ReactNode }) {
  const r = w.res
  const st = stateOf(w)
  const on = w.connected && !w.revoked_at
  return (
    <li data-testid={`worker-card-${w.id}`} className={`flex flex-col gap-2 px-4 py-3 ${w.revoked_at ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5">
            <span className="truncate font-medium">{w.name}</span>
            <KindTag w={w} />
          </p>
          <p className="truncate font-mono text-xs text-muted">{w.id}{svc(w)}{w.gpu ? ` · ${w.gpu}` : ''}</p>
          {keyMissing(w) && <p className="text-xs text-warn" data-testid={`worker-nokey-${w.id}`}>▲ No key: no jobs</p>}
          {w.cost_stopped && <p className="text-xs text-fail" data-testid={`worker-budget-${w.id}`}>■ Budget stop: no new jobs</p>}
        </div>
        <StateBadge spec={SPEC[st]} />
        <button type="button" aria-expanded={open} aria-label={`Details of ${w.name}`} onClick={onToggle}
          className={`grid h-11 w-11 flex-none place-items-center rounded-md border ${open ? 'border-accent text-accent' : 'border-line-2 text-muted'}`}>
          <GearIcon />
        </button>
      </div>
      {on && (
        <div className="grid grid-cols-3 gap-3">
          <Fact k="Slots"><Slots w={w} /></Fact>
          <Fact k="CPU"><span className="num font-mono">{pctText(r?.cpu_pct)}</span></Fact>
          <Fact k="GPU"><span className="num font-mono">{pctText(r?.gpu_pct)}</span></Fact>
          <Fact k="RAM"><span className="num font-mono">{mb(r?.ram_used_mb)}</span></Fact>
          <Fact k="VRAM"><span className="num font-mono">{mb(r?.gpu_mem_used_mb)}</span></Fact>
          <Fact k="Traffic"><Traffic w={w} /></Fact>
          {w.cost && <Fact k="Cost, month"><span className="num font-mono">{usd(w.cost.month_micros)}</span></Fact>}
        </div>
      )}
      {children}
    </li>
  )
}

function WorkerDetails({ w, keys, accounts, onAssign, onAccount, confirm, onConfirm, onRevoke }: {
  w: CaptionsWorkerInfo
  keys: CaptionsAPIKey[]
  accounts: CostAccount[]
  onAssign: (keyID: string | null) => Promise<void>
  onAccount: (accountID: string | null) => Promise<void>
  confirm: boolean
  onConfirm: (v: boolean) => void
  onRevoke: () => void
}) {
  const hist = w.history ?? []
  const ax = onAxis<WorkerSample>(hist, 5000)
  const series = (f: (p: WorkerSample) => number | undefined) => ax.t.map((_, i) => {
    const p = ax.at(i)
    const v = p ? f(p) : undefined
    return v == null ? null : v
  })
  const r = w.res
  return (
    <div className="flex flex-col gap-4" data-testid={`worker-details-${w.id}`}>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4 lg:grid-cols-6">
        <Fact k="Kind">{w.kind ? `${KIND[w.kind]?.label ?? w.kind} — ${KIND[w.kind]?.hint ?? ''}` : '—'}</Fact>
        <Fact k="System">{w.host?.os || '—'}</Fact>
        <Fact k="CPU">{w.host?.cpu_cores ? `${w.host.cpu_cores} cores` : '—'}</Fact>
        <Fact k="Memory">{mb(w.host?.ram_mb ?? r?.ram_total_mb)}</Fact>
        <Fact k="Video card">{w.gpu ? `${w.gpu}${w.host?.gpu_mem_mb ? ` · ${mb(w.host.gpu_mem_mb)}` : ''}` : '—'}</Fact>
        <Fact k="Languages">{w.languages.length ? w.languages.map(langName).join(', ') : '—'}</Fact>
        <Fact k="Traffic since start">
          <span className="num font-mono text-[13px]" data-testid={`worker-total-${w.id}`}>↓ {bytes(r?.rx_bytes)} · ↑ {bytes(r?.tx_bytes)}</span>
        </Fact>
        <Fact k="Report">{r ? ago(r.at) : '—'}</Fact>
      </div>

      {w.kind === 'api' && !w.revoked_at && <KeyPicker w={w} keys={keys} onAssign={onAssign} />}
      {!w.revoked_at && <WorkerCostBlock w={w} keys={keys} accounts={accounts} onAssign={onAccount} />}

      {hist.length > 1 && (
        <div className="grid gap-4 lg:grid-cols-3" data-testid={`worker-charts-${w.id}`}>
          <LineChart title="Load" unit="%" t={ax.t} min={0} max={100} fmt={(v) => `${Math.round(v)}`} label={`CPU and video card load of ${w.name}, last 5 minutes`}
            series={[{ name: 'CPU', values: series((p) => p.cpu_pct), stroke: 'stroke-text-soft' }, { name: 'Video card', values: series((p) => p.gpu_pct), stroke: 'stroke-accent', dash: '6 3' }]} />
          <LineChart title="Memory" unit="GB" t={ax.t} min={0} fmt={(v) => (v / 1024).toFixed(1)} label={`RAM and video memory of ${w.name}, last 5 minutes`}
            series={[{ name: 'RAM', values: series((p) => p.ram_used_mb), stroke: 'stroke-text-soft' }, { name: 'Video memory', values: series((p) => p.gpu_mem_used_mb), stroke: 'stroke-accent', dash: '6 3' }]} />
          <LineChart title="Traffic" t={ax.t} min={0} fmt={(v) => bps(Math.round(v))} label={`Traffic of ${w.name}, last 5 minutes`}
            series={[{ name: 'In', values: series((p) => p.rx_bps), stroke: 'stroke-text-soft' }, { name: 'Out', values: series((p) => p.tx_bps), stroke: 'stroke-accent', dash: '6 3' }]} />
        </div>
      )}

      <div>
        <h3 className="mono-label mb-2 uppercase">Models</h3>
        {w.model_info?.length ? (
          <table className="hidden w-full table-fixed text-left text-[13px] sm:table" data-testid={`worker-model-list-${w.id}`}>
            <thead className="text-xs text-muted">
              <tr>
                <th className="w-[44%] py-1 pr-3 font-medium">Model</th>
                <th className="py-1 pr-3 font-medium">Type</th>
                <th className="py-1 pr-3 font-medium">Runs on</th>
                <th className="py-1 pr-3 text-right font-medium">Size</th>
                <th className="py-1 font-medium">State</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {w.model_info.map((m) => (
                <tr key={m.name} className="[&>td]:py-1.5 [&>td]:pr-3 [&>td]:align-top">
                  <td className="truncate font-mono text-xs" title={m.name}>{m.name}</td>
                  <td>{MODEL_TYPE[m.type]}</td>
                  <td className="uppercase">{m.device ?? '—'}</td>
                  <td className="num text-right font-mono text-xs">{m.size_mb != null ? mb(m.size_mb) : m.device === 'api' ? 'service' : '—'}</td>
                  <td className={`text-xs ${m.ready ? 'text-text-soft' : 'text-warn'}`}>{m.ready ? 'Ready' : m.device === 'api' ? 'Not configured' : 'Not downloaded'}</td>
                </tr>
              ))}
            </tbody>
            {modelsTotal(w) > 0 && (
              <tfoot>
                <tr className="text-xs text-muted">
                  <td colSpan={3} className="pt-1.5">On disk</td>
                  <td className="num pt-1.5 pr-3 text-right font-mono">{mb(modelsTotal(w))}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        ) : null}
        {w.model_info?.length ? (
          <ul className="divide-y divide-line text-[13px] sm:hidden" data-testid={`worker-model-cards-${w.id}`}>
            {w.model_info.map((m) => (
              <li key={m.name} className="flex flex-col gap-0.5 py-1.5">
                <span className="font-mono text-xs break-all">{m.name}</span>
                <span className="text-xs text-muted">
                  {MODEL_TYPE[m.type]} · {(m.device ?? '—').toUpperCase()} · {m.size_mb != null ? mb(m.size_mb) : m.device === 'api' ? 'service' : '—'} ·{' '}
                  <span className={m.ready ? 'text-text-soft' : 'text-warn'}>{m.ready ? 'Ready' : m.device === 'api' ? 'Not configured' : 'Not downloaded'}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">{w.models.length ? `${w.models.join(', ')} (this worker version does not say their type and size)` : 'Shown while the worker is connected.'}</p>
        )}
      </div>

      {w.jobs.length > 0 && (
        <div>
          <h3 className="mono-label mb-2 uppercase">Jobs</h3>
          <ul className="space-y-1 text-[13px]" data-testid={`worker-jobs-${w.id}`}>
            {w.jobs.map((j) => (
              <li key={j.job_id} className="num">
                <span className="font-mono">{j.stream}</span> on {j.node} · {langName(j.language)}
                {j.detected_language && j.language === 'auto' ? ` (${langName(j.detected_language)})` : ''} · <StateText spec={JOB_SPEC[j.state]} />
                {j.rtf != null && j.rtf > 0 ? ` · RTF ${j.rtf.toFixed(2)}` : ''}
                {j.lag_ms ? ` · ${(j.lag_ms / 1000).toFixed(1)} s behind` : ''}
                {j.last_error && <span className="text-fail"> · {j.last_error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!w.revoked_at && (
        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {confirm ? (
            <>
              <span className="text-sm text-text-soft">Its channel closes now; its jobs move to other workers.</span>
              <Button variant="danger" onClick={onRevoke}>Revoke and disconnect</Button>
              <Button variant="quiet" onClick={() => onConfirm(false)}>Cancel</Button>
            </>
          ) : (
            <Button variant="quiet" onClick={() => onConfirm(true)}>Revoke</Button>
          )}
        </div>
      )}
    </div>
  )
}

function JobsTable({ jobs }: { jobs: (CaptionsJobAdmin & { worker: CaptionsWorkerInfo })[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-left text-sm" data-testid="captions-jobs-table">
        <thead className="border-b border-line text-xs text-muted">
          <tr>
            {['Stream', 'Media node', 'Worker', 'Language', 'State', 'RTF', 'Behind', 'Traffic'].map((h) => (
              <th key={h} scope="col" className="px-3 py-2 font-medium whitespace-nowrap first:pl-4 last:pr-4">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {jobs.map((j) => (
            <tr key={j.job_id} data-testid={`job-${j.job_id}`} className="[&>td]:px-3 [&>td]:py-2 [&>td]:align-top [&>td:first-child]:pl-4 [&>td:last-child]:pr-4">
              <td className="font-mono text-[13px]">{j.stream}</td>
              <td className="font-mono text-[13px]">{j.node || '—'}</td>
              <td className="truncate">{j.worker.name}</td>
              <td>
                {langName(j.language)}
                {j.detected_language && j.language === 'auto' ? <span className="text-muted"> ({langName(j.detected_language)})</span> : null}
              </td>
              <td>
                <StateText spec={JOB_SPEC[j.state]} />
                {j.last_error && <p className="max-w-[28ch] truncate text-xs text-fail" title={j.last_error}>{j.last_error}</p>}
              </td>
              <td className="num font-mono text-[13px]">{j.rtf != null && j.rtf > 0 ? j.rtf.toFixed(2) : '—'}</td>
              <td className="num font-mono text-[13px]">{j.lag_ms ? `${(j.lag_ms / 1000).toFixed(1)} s` : '—'}</td>
              <td className="num font-mono text-[12px] whitespace-nowrap">{j.rx_bytes != null ? `↓ ${bytes(j.rx_bytes)} · ↑ ${bytes(j.tx_bytes)}` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** "API key" in an API node's details: pick one of the stored keys of its service. */
function KeyPicker({ w, keys, onAssign }: { w: CaptionsWorkerInfo; keys: CaptionsAPIKey[]; onAssign: (keyID: string | null) => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  // a node that has not said its service yet may take any key; the center checks the pair on its next hello
  const fit = keys.filter((k) => !w.provider || k.provider === w.provider)
  const cur = keys.find((k) => k.id === w.api_key_id)
  const change = async (v: string) => {
    setBusy(true)
    try {
      await onAssign(v || null)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line p-3" data-testid={`worker-key-${w.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="mono-label uppercase">{w.provider ? `${PROVIDERS[w.provider]} key` : 'Service key'}</h3>
        <select aria-label={`API key of ${w.name}`} data-testid={`worker-key-select-${w.id}`} className={SEL} disabled={busy} value={w.api_key_id ?? ''} onChange={(e) => void change(e.target.value)}>
          <option value="">No key</option>
          {fit.map((k) => (
            <option key={k.id} value={k.id}>
              {!w.provider ? `${PROVIDERS[k.provider]} · ` : ''}
              {k.label} · …{k.last4}
              {k.used_by.filter((id) => id !== w.id).length ? ` · shared with ${k.used_by.filter((id) => id !== w.id).length}` : ''}
            </option>
          ))}
        </select>
        {cur && cur.used_by.length > 1 && (
          <span className="text-xs text-muted" data-testid={`worker-key-shared-${w.id}`}>Shared by {cur.used_by.length} nodes</span>
        )}
      </div>
      {keyMissing(w) ? (
        <p className="text-xs text-warn">{fit.length ? 'Pick a key: without one the node takes no jobs.' : `Add a ${w.provider ? PROVIDERS[w.provider] : 'service'} key in API keys below: without one the node takes no jobs.`}</p>
      ) : w.api_key_id && w.connected && w.key_ready ? (
        <p className="text-xs text-text-soft">The node has the key and takes jobs.</p>
      ) : w.api_key_id && !w.connected ? (
        <p className="text-xs text-muted">The node gets the key when it connects.</p>
      ) : null}
    </div>
  )
}

/** Network › Captions › API keys: stored keys of external services and the nodes that use each. */
function APIKeysPanel({ keys, loading, error, workers, onChanged, accounts, onAccount }: {
  accounts: CostAccount[]
  onAccount: (keyID: string, accountID: string | null) => Promise<void>
  keys?: CaptionsAPIKey[]
  loading: boolean
  error: Error | null
  workers: CaptionsWorkerInfo[]
  onChanged: () => Promise<unknown>
}) {
  const [adding, setAdding] = useState(false)
  const [provider, setProvider] = useState<CaptionsProvider>('assemblyai')
  const [label, setLabel] = useState('')
  const [secret, setSecret] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const name = (id: string) => workers.find((w) => w.id === id)?.name ?? id
  const add = async () => {
    setErr('')
    setBusy(true)
    try {
      await post<CaptionsAPIKey>('/api/admin/captions/api-keys', { provider, label: label.trim(), key: secret.trim() })
      setLabel('')
      setSecret('')
      setAdding(false)
      await onChanged()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const remove = async (id: string) => {
    setErr('')
    try {
      await del(`/api/admin/captions/api-keys/${id}`)
      await onChanged()
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  const list = keys ?? []
  return (
    <NetPanel
      pad={false}
      title="API keys"
      testId="captions-api-keys"
      aside={
        <Button variant="plain" data-testid="api-key-add" aria-expanded={adding} className="max-md:min-h-[44px]" onClick={() => setAdding((a) => !a)}>
          {adding ? 'Close' : 'Add key'}
        </Button>
      }
    >
      {err && <div className="px-4 pt-3"><ErrorBox text={err} /></div>}
      {error && <div className="px-4 pt-3"><ErrorBox text={error.message} /></div>}
      {adding && (
        <form
          className="grid gap-2 border-b border-line p-4 sm:grid-cols-[auto_1fr_1.4fr_auto] sm:items-end"
          data-testid="api-key-form"
          onSubmit={(e) => {
            e.preventDefault()
            void add()
          }}
        >
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Service</span>
            <select name="api-key-provider" className={`${SEL} w-full`} value={provider} onChange={(e) => setProvider(e.target.value as CaptionsProvider)}>
              {(Object.keys(PROVIDERS) as CaptionsProvider[]).map((p) => <option key={p} value={p}>{PROVIDERS[p]}</option>)}
            </select>
          </label>
          <Field label="Label" name="api-key-label" value={label} maxLength={64} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Main account" />
          <Field label="Key" name="api-key-secret" type="password" autoComplete="off" spellCheck={false} value={secret} maxLength={1024} onChange={(e) => setSecret(e.target.value)} placeholder="Pasted once, never shown again" />
          <Button variant="primary" type="submit" disabled={busy || !label.trim() || secret.trim().length < 8} className="max-md:min-h-[44px]">Save key</Button>
        </form>
      )}
      {loading ? (
        <div className="p-4"><SkeletonRows rows={2} cols={4} /></div>
      ) : list.length === 0 ? (
        <p className="p-4 text-sm text-muted" data-testid="api-keys-empty">
          No keys. An API node relays audio to AssemblyAI, Soniox, Deepgram or OpenAI with a key stored here; one key can serve several nodes.
        </p>
      ) : (
        <ul className="divide-y divide-line" data-testid="api-keys-list">
          {list.map((k) => (
            <li key={k.id} data-testid={`api-key-${k.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
              <span className="w-24 flex-none font-medium">{PROVIDERS[k.provider] ?? k.provider}</span>
              <span className="min-w-32 flex-1 truncate" title={k.label}>{k.label}</span>
              <span className="num font-mono text-[13px] text-muted" title="The last characters of the key">…{k.last4}</span>
              <span className="min-w-40 text-[13px]" data-testid={`api-key-used-${k.id}`}>
                {k.used_by.length === 0 ? (
                  <span className="text-muted">Not used</span>
                ) : (
                  <span title={k.used_by.map(name).join(', ')}>
                    {k.used_by.length > 1 ? <span className="font-medium">{k.used_by.length} nodes: </span> : null}
                    {k.used_by.map(name).join(', ')}
                  </span>
                )}
              </span>
              <AccountSelect accounts={accounts} kind="api" provider={k.provider} value={k.account_id} label={`Cost account of the key ${k.label}`}
                testId={`api-key-account-${k.id}`} onChange={(a) => onAccount(k.id, a)} />
              <span className="text-xs text-muted">{ago(k.created_at)}</span>
              <Button
                variant="quiet"
                data-testid={`api-key-delete-${k.id}`}
                disabled={k.used_by.length > 0}
                title={k.used_by.length > 0 ? 'Used by nodes: assign them another key first' : 'Delete the key'}
                onClick={() => void remove(k.id)}
              >
                Delete
              </Button>
            </li>
          ))}
        </ul>
      )}
    </NetPanel>
  )
}
